/**
 * Utility functions for working with dates in the application
 */

/**
 * Formats a date as "YYYY-MM-DD"
 * @param date The date to format
 * @returns Formatted date string
 */
export const formatDateISO = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Formats a date in a user-friendly format (e.g., "Monday, April 20, 2025")
 * @param date The date to format
 * @returns Formatted date string
 */
export const formatDateLong = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Formats a date in a short format (e.g., "Apr 20")
 * @param date The date to format
 * @returns Formatted date string
 */
export const formatDateShort = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Gets the day of the week for a date
 * @param date The date to get the day of week for
 * @returns Day of week string
 */
export const getDayOfWeek = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

/**
 * Checks if two dates are the same day
 * @param date1 First date
 * @param date2 Second date
 * @returns True if dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Gets a date range for the next n days
 * @param startDate Starting date
 * @param days Number of days in the range
 * @returns Array of dates
 */
export const getDateRange = (startDate: Date = new Date(), days: number = 7): Date[] => {
  const dates: Date[] = [];
  
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    dates.push(date);
  }
  
  return dates;
};
